<template>
  <div class="invoice invoice--view">
    <vue-headful :title="`Invoice - ${invoiceStreetAddress}`" />
    <ViewComponentLoader v-if="apiLoading" label="Loading your Invoice" />
    <template v-if="apiLoaded">
      <div>
        <AddressTitleBar
          class="title"
          :hg-address="invoiceStreetAddress"
          :hg-location="invoiceLocation"
          :hg-logo="company.logo">
          <hg-inspection-button :hg-folder-id="folderId"/>
        </AddressTitleBar>
      </div>

      <v-layout>
        <v-flex md4 offset-md4 class="header">
          <h1 class="test-invoice-header">Invoice</h1>
          <h2>You have an outstanding balance of ${{invoiceTotalDue}}.</h2>
        </v-flex>
      </v-layout>

      <v-container>
        <v-layout row wrap>
          <v-flex xs12 md10 offset-md1>
            <hg-receipt class="receipt-page" :hg-receipt="invoice" :hg-company="company"/>
          </v-flex>
        </v-layout>
      </v-container>

      <v-layout xs2 md4 class="row">
        <v-flex md2 offset-md2 offset-xs1>
          <h3><div>PAYMENTS</div></h3>
        </v-flex>

        <v-flex md6 offset-md1 offset-xs1>
          <p class="payment-header">To pay this invoice, please contact your inspection company.</p>
          <p class="payment-item company-name">{{company.name}}</p>
          <p class="payment-item company-address1">{{company.address1}}</p>
          <p class="payment-item company-address2">{{company.address2}}</p>
          <p class="payment-item company-city">
            {{company.city}},
            {{company.state}}
            {{company.postalCode}}
          </p>
          <p class="payment-item company-email">{{company.email}}</p>
          <p class="payment-item company-primaryPhone">{{company.primaryPhone}}</p>
        </v-flex>
      </v-layout>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import BaseViewComponent from '@/components/Shared/BaseViewComponent.vue';
import ViewComponentLoader from '@/components/Shared/ViewComponentLoader.vue';
import AddressTitleBar from '../AddressBar/AddressTitleBar.vue';
import InspectionButton from '../AddressBar/InspectionButton.vue';
import Receipt from '../Payments/Receipt.vue';
import Store from '../../store';

export default {
  name: 'hgInvoice',
  extends: BaseViewComponent,
  mounted() {
    this.initialHydration([
      Store.dispatch('Invoice/loadInvoice', this.folderId),
      Store.dispatch('Company/loadCompany', this.companyId),
    ]);
  },
  components: {
    'hg-inspection-button': InspectionButton,
    'hg-receipt': Receipt,
    ViewComponentLoader,
    AddressTitleBar,
  },
  props: {
    companyId: {
      type: String,
      required: true,
    },
    folderId: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState('Company', {
      company: state => state.company,
    }),
    ...mapState('Invoice', {
      invoice: state => state.invoice,
    }),
    invoiceStreetAddress() {
      if ((!this.invoice || !this.invoice.folder)
      || (!this.invoice.folder.address1 && !this.invoice.folder.address2)) {
        return '';
      }
      if (this.invoice.folder.address1 && this.invoice.folder.address2) {
        return `${this.invoice.folder.address1}, ${this.invoice.folder.address2}`;
      }
      return (this.invoice.folder.address1 ? `${this.invoice.folder.address1}` : `${this.invoice.folder.address2}`);
    },
    invoiceLocation() {
      if (!this.invoice || !this.invoice.folder) {
        return '';
      }
      if (!this.invoice.folder.city) {
        return `${this.invoice.folder.state} ${this.invoice.folder.postalCode}`;
      }
      if (!this.invoice.folder.state && !this.invoice.folder.postalCode) {
        return this.invoice.folder.city;
      }
      return `${this.invoice.folder.city}, ${this.invoice.folder.state} ${this.invoice.folder.postalCode}` || '';
    },
    invoiceTotalDue() {
      const trimAmountToTwoDigits = 2;
      if (!this.invoice || !this.invoice.totalAmountDue) {
        return 0;
      }
      return this.invoice.totalAmountDue.toFixed(trimAmountToTwoDigits);
    },
  },
  beforeRouteUpdate(to, from, next) {
    if (!to.hash) { /* ignore anchor tags for scrolling */
      this.created();
      next();
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';
@import '../../styles/vars/typography';

.invoice {
  color: lighten($theme-color-gray, 19.22);
  font-size: 1.214rem;
  padding-bottom: 20px;
}

.header {
  padding-top: 40px;
  text-align: center;
}

h1 {
  font-size: 2.5rem;
  text-transform: none;
}

h2 {
  color: rgba(0, 0, 0, 0.6);
  font-size: 1.286rem;
  font-weight: normal;
  padding-top: 20px;
}

p.payment-item {
  margin: 0;
}

@media all and (max-width: 655px) {
  .layout {
    flex-direction: column;
  }
}
</style>
